// Sprint-15 — team-watch client state.
//
// One store owns:
//   • watchedGuideIds — Set of guide ids the current user has marked watched.
//                       Used by MarkWatchedButton to render the right label
//                       and by Dashboard's `?filter=unwatched` lens.
//   • watchedAt       — guide_id → ISO string. Drives "Watched · 2h ago".
//   • unwatchedCount  — top-nav badge value; refreshed on app focus + a 60s
//                       interval. Decrements optimistically when the user
//                       clicks "Mark watched" so the badge feels instant.
//
// IMPORTANT — every consumer reads with INDIVIDUAL selectors only. Never write
//   const { foo, bar } = useWatch(s => ({ foo: s.foo, bar: s.bar }))
// because returning a new object every render triggers Zustand's
// shallow-equality re-render loop. Project rule: one selector per primitive.

import { create } from 'zustand';
import { watchesApi } from '../services/watchesApi';

// FE-rel-24 — monotonic token so a slow unwatched-count refresh that resolves
// after a fresher one can't clobber the newer badge value.
let unwatchedRefreshSeq = 0;

interface WatchState {
  // Lazy-loaded — populated by the first hydrateWatched call. We treat an
  // empty Set as "loaded but no watches", and `hydrated` separates that from
  // "haven't asked the server yet".
  watchedGuideIds: Set<string>;
  watchedAt: Record<string, string>;
  hydrated: boolean;

  unwatchedCount: number;

  // Local optimistic updates. Server is the source of truth — every call below
  // patches local state first, then PATCHes/POSTs, then rolls back on failure.
  markWatched: (guideId: string, isOwnGuide?: boolean) => Promise<void>;
  unwatch: (guideId: string, isOwnGuide?: boolean) => Promise<void>;
  refreshUnwatchedCount: () => Promise<void>;

  /**
   * Pull watched_at for a list of guide ids. Cheap: we call /watches per
   * guide id and merge — used by the viewer toolbar on mount. The dashboard
   * also calls this on first load with the full guide list so the
   * "Watched" pill renders correctly on every card.
   *
   * For v1 we do one HTTP roundtrip per guide; if this becomes hot we'll add
   * a batched /api/v1/me/watches endpoint. The pill works without hydration
   * (it just defaults to "Mark watched"), so this is a progressive-enhance.
   */
  hydrateFromGuides: (guideIds: string[], currentUserId: string) => Promise<void>;
}

export const useWatch = create<WatchState>((set, get) => ({
  watchedGuideIds: new Set<string>(),
  watchedAt: {},
  hydrated: false,
  unwatchedCount: 0,

  markWatched: async (guideId, isOwnGuide) => {
    const prev = {
      ids: get().watchedGuideIds,
      at:  get().watchedAt,
      cnt: get().unwatchedCount,
    };
    if (prev.ids.has(guideId)) return; // already watched — no-op
    // Optimistic: add to set, stamp watched_at, decrement count (only if it's
    // someone else's guide — watching your own doesn't count toward the badge).
    const nextIds = new Set(prev.ids); nextIds.add(guideId);
    set({
      watchedGuideIds: nextIds,
      watchedAt: { ...prev.at, [guideId]: new Date().toISOString() },
      unwatchedCount: isOwnGuide ? prev.cnt : Math.max(0, prev.cnt - 1),
    });
    try {
      const r = await watchesApi.markWatched(guideId);
      // Echo server's watched_at back so relative-time matches DB state.
      set((s) => ({ watchedAt: { ...s.watchedAt, [guideId]: r.watched_at } }));
    } catch (e) {
      // Roll back the optimistic state — keep the UI honest with the server.
      set({
        watchedGuideIds: prev.ids,
        watchedAt: prev.at,
        unwatchedCount: prev.cnt,
      });
      console.error('[watchStore.markWatched] failed', e);
    }
  },

  unwatch: async (guideId, isOwnGuide) => {
    const prev = {
      ids: get().watchedGuideIds,
      at:  get().watchedAt,
      cnt: get().unwatchedCount,
    };
    if (!prev.ids.has(guideId)) return; // not watched — no-op
    const nextIds = new Set(prev.ids); nextIds.delete(guideId);
    const nextAt = { ...prev.at }; delete nextAt[guideId];
    set({
      watchedGuideIds: nextIds,
      watchedAt: nextAt,
      // Re-include the row in the badge count if it's a team capture.
      unwatchedCount: isOwnGuide ? prev.cnt : prev.cnt + 1,
    });
    try {
      await watchesApi.unwatch(guideId);
    } catch (e) {
      set({
        watchedGuideIds: prev.ids,
        watchedAt: prev.at,
        unwatchedCount: prev.cnt,
      });
      console.error('[watchStore.unwatch] failed', e);
    }
  },

  refreshUnwatchedCount: async () => {
    const seq = ++unwatchedRefreshSeq;
    try {
      const n = await watchesApi.unwatchedCount();
      if (seq !== unwatchedRefreshSeq) return; // superseded by a newer refresh
      set({ unwatchedCount: n });
    } catch (e) {
      // Silent — badge just shows the last known value rather than throwing.
      console.warn('[watchStore.refreshUnwatchedCount] failed', e);
    }
  },

  hydrateFromGuides: async (guideIds, currentUserId) => {
    if (get().hydrated) return;
    set({ hydrated: true }); // optimistic — prevents thrash on remount
    try {
      // Hydration walks the guide list and asks the server "who watched this?".
      // We pluck the current user's row out of each response. One roundtrip
      // per guide; acceptable up to ~50-guide pages.
      const watchedIds = new Set<string>();
      const watchedAt: Record<string, string> = {};
      for (const id of guideIds) {
        try {
          const ws = await watchesApi.listWatchers(id);
          const mine = ws.find(w => w.user_id === currentUserId);
          if (mine) {
            watchedIds.add(id);
            watchedAt[id] = mine.watched_at;
          }
        } catch {/* per-guide failures are noisy but non-fatal */}
      }
      set((s) => ({
        watchedGuideIds: new Set([...s.watchedGuideIds, ...watchedIds]),
        watchedAt: { ...s.watchedAt, ...watchedAt },
      }));
    } catch (e) {
      set({ hydrated: false }); // allow retry on next call
      console.warn('[watchStore.hydrateFromGuides] failed', e);
    }
  },
}));

/**
 * Bootstraps focus-refresh + 60s polling for the unwatched-count badge.
 * Call once from AppShell on mount. Returns a teardown to unwire.
 */
export function attachWatchBadgePollers(): () => void {
  const refresh = () => useWatch.getState().refreshUnwatchedCount();
  refresh();
  const onFocus = () => refresh();
  window.addEventListener('focus', onFocus);
  // 60s when the tab is active. Browsers throttle this to ~60s minimum when
  // the tab is hidden anyway, so we don't bother with a visibilitychange hook.
  const id = window.setInterval(refresh, 60_000);
  return () => {
    window.removeEventListener('focus', onFocus);
    window.clearInterval(id);
  };
}
